import { createContext, useContext, useRef, useSyncExternalStore, type ReactNode } from 'react';
import type { DocumentUploadStatus } from '../types';

// Tracks one file binary upload that is in-flight or recently completed.
// objectId is the stable server-assigned key that survives component remounts.
export interface ActiveUpload {
  objectId: string;
  fileId: string;
  fileName: string;
  status: DocumentUploadStatus;
  progress: number;
}

type UploadListener = () => void;

// Holds all active uploads outside the React lifecycle so uploads survive sidepanel close/reopen.
// Snapshot immutability: this.snapshot is rebuilt on every mutation so useSyncExternalStore
// can bail out when nothing changed.
class UploadRegistry {
  private uploads = new Map<string, ActiveUpload>();
  private listeners = new Set<UploadListener>();
  private snapshot: ReadonlyMap<string, ActiveUpload> = new Map();

  subscribe = (listener: UploadListener): (() => void) => {
    this.listeners.add(listener);
    return () => this.listeners.delete(listener);
  };

  getSnapshot = (): ReadonlyMap<string, ActiveUpload> => this.snapshot;

  // Creates or replaces an entry. Calling this again (re-upload) resets progress to 0.
  register(entry: ActiveUpload): void {
    this.uploads.set(entry.objectId, entry);
    this.notify();
  }

  // Moves progress forward. No-op if objectId is not registered.
  updateProgress(objectId: string, progress: number): void {
    const existing = this.uploads.get(objectId);
    if (!existing) return;
    this.uploads.set(objectId, { ...existing, progress });
    this.notify();
  }

  // Marks the upload as completed, then removes the entry after a short delay
  // so the indicator briefly shows 100% before disappearing.
  complete(objectId: string): void {
    const existing = this.uploads.get(objectId);
    if (!existing) return;
    this.uploads.set(objectId, { ...existing, status: 'uploaded', progress: 100 });
    this.notify();
    setTimeout(() => {
      this.uploads.delete(objectId);
      this.notify();
    }, 1500);
  }

  // Marks the upload as failed. Entry stays until a re-upload replaces it.
  fail(objectId: string): void {
    const existing = this.uploads.get(objectId);
    if (!existing) return;
    this.uploads.set(objectId, { ...existing, status: 'failed' });
    this.notify();
  }

  // Immediately removes the entry. Used when the object is being deleted so
  // the indicator doesn't linger after a delete-while-uploading.
  remove(objectId: string): void {
    if (!this.uploads.has(objectId)) return;
    this.uploads.delete(objectId);
    this.notify();
  }

  // Returns all entries for a specific file (used for reconnection on remount).
  getForFile(fileId: string): ActiveUpload[] {
    return Array.from(this.uploads.values()).filter((u) => u.fileId === fileId);
  }

  private notify(): void {
    this.snapshot = new Map(this.uploads);
    this.listeners.forEach((l) => l());
  }
}

const UploadRegistryContext = createContext<UploadRegistry | undefined>(undefined);

// Provides the upload registry to the documents feature subtree.
// Uses useRef so the registry instance never triggers re-renders of the provider itself.
export function UploadRegistryProvider({ children }: { children: ReactNode }) {
  const registryRef = useRef<UploadRegistry>(new UploadRegistry());
  return (
    <UploadRegistryContext.Provider value={registryRef.current}>
      {children}
    </UploadRegistryContext.Provider>
  );
}

// Returns the registry instance, or null when called outside the provider.
// Allows useFileObjectRows to work in tests and other contexts without the provider.
export function useOptionalUploadRegistry(): UploadRegistry | null {
  return useContext(UploadRegistryContext) ?? null;
}

// Subscribes reactively to the registry snapshot via useSyncExternalStore.
// Used by UploadProgressIndicator to re-render on every progress update.
export function useActiveUploads(): ReadonlyMap<string, ActiveUpload> {
  const registry = useContext(UploadRegistryContext);
  const emptyMap: ReadonlyMap<string, ActiveUpload> = new Map();

  // When outside the provider, return a stable empty map.
  return useSyncExternalStore(
    registry ? registry.subscribe : () => () => {},
    registry ? registry.getSnapshot : () => emptyMap
  );
}
